import React from 'react';
import { Button } from '../ui/Button';
import { TransactionItem } from '../../models/TransactionItem';
import { exportToExcel, ExcelColumnConfig } from '../../utils/excelExport';
import downloadIcon from '../../assets/images/download.png';
import plus from '../../assets/images/plus.png';
import arrowRight from '../../assets/images/arrow_right_grey.png';
import { useNavigate } from 'react-router-dom';

interface TransactionsHeaderProps {
  transactions?: TransactionItem[];
  recordName?: string;
}

const TransactionsHeader: React.FC<TransactionsHeaderProps> = ({ transactions = [], recordName }) => {
  const navigate = useNavigate();

  const formatQuantity = (quantity?: number) => {
    if (quantity === undefined || quantity === null) return '';
    return quantity.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const onNewTransaction = () => {
    navigate('/records/transactions/new');
  };

  const handleExportAll = () => {
    if (transactions.length === 0) return;

    const columns: ExcelColumnConfig<TransactionItem>[] = [
      { key: 'id', header: 'Transaction ID', transform: (_, row) => row.getHashedId() },
      {
        key: 'dateTime',
        header: 'Date',
        transform: (v) => {
          if (!v) return '';
          const d = new Date(v);
          return isNaN(d.getTime()) ? v : d.toLocaleDateString();
        },
      },
      {
        key: 'dateTime',
        header: 'Time',
        transform: (v) => {
          if (!v) return '';
          const d = new Date(v);
          return isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
        },
      },
      { key: 'action', header: 'Action', transform: (v) => v?.transaction_entity_name || '' },
      { key: 'direct_object', header: 'Object', transform: (v) => v?.transaction_entity_name || '' },
      { key: 'indirect_object', header: 'Vendor', transform: (v) => v?.transaction_entity_name || '' },
      { key: 'qty', header: 'Quantity', transform: (v) => formatQuantity(v) },
      { key: 'paymentMedia', header: 'Payment Media', transform: (v) => v?.transaction_entity_name || '' },
            { key: 'amount', header: 'Amount', transform: (v) => v ?? '' },
      { key: 'currency', header: 'Currency', transform: (v) => v || '' },
    ];

    exportToExcel({
      data: transactions,
      columns,
      fileName: 'transactions',
      sheetName: 'Transactions',
      includeTimestamp: true,
    });
  };

  return (
    <div className="py-4 bg-light rounded-t-lg flex flex-col sm:flex-row justify-between items-start sm:items-center">
      <div className="w-full sm:w-auto">
        <h3 className="text-xl mx-0 sm:mx-4 flex items-center gap-2">
          {recordName ? (
            <>
              <span>{recordName}</span>
              <img
                src={arrowRight}
                alt=""
                className="rounded-full object-cover"
                style={{ width: '12px', height: '12px' }}
              />
            </>
          ) : null}
          <span>Transactions</span>
        </h3>
        <p className="text-grey_dark mx-0 sm:mx-4 mt-1 text-sm sm:hidden md:block">
          Manage and track all your financial transactions
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        {transactions.length > 0 && (
          <Button
            onClick={handleExportAll}
            variant="google"
            className="flex items-center w-full sm:w-auto justify-center transition-all duration-200"
          >
            
            Export
          </Button>
        )}
        <Button
          onClick={onNewTransaction}
          variant="filled-primary"
          className="flex items-center w-full sm:w-auto justify-center transition-all duration-200 hover:scale-[1.02]"
        >
          <img
            src={plus}
            alt="Add"
            className="object-contain w-3 h-3 mr-2 transition-transform duration-300"
          />
          Add Transaction
        </Button>
      </div>
    </div>
  );
};

export default TransactionsHeader;
